import type { RemoteViewLayout } from './nativeLayout.ts';

/**
 * Stand-zone shape — mirrors the native apps' `CutoutArea` geometry so the box on TV
 * matches the box drawn on the phone (values are fractions of the game rect):
 *  - `'upper'`  — fruit-slice: 0.4w × 0.8h, centred (upper-body silhouette).
 *  - `'full'`   — target-attack: 0.4w × 0.8h, centred (full-body silhouette).
 *  - `'square'` — runner / leg-kick: 1:1 square at 0.85 of the height, centred (full body).
 */
export type CalibStandZoneKind = 'full' | 'upper' | 'square';

/** Stand zone for calibration / start screens (pixels in layout space). */
export interface RemoteCalibStandZoneMetrics {
  readonly cx: number;
  readonly cy: number;
  readonly boxX: number;
  readonly boxY: number;
  readonly boxW: number;
  readonly boxH: number;
  readonly borderW: number;
}

export function getRemoteCalibStandZoneMetrics(
  layout: RemoteViewLayout,
  kind: CalibStandZoneKind = 'full',
): RemoteCalibStandZoneMetrics {
  const {
    offsetX, offsetY, gameWidth, gameHeight,
  } = layout;
  const cx = offsetX + gameWidth / 2;
  const cy = offsetY + gameHeight / 2;

  let boxW: number;
  let boxH: number;
  if (kind === 'square') {
    // Native runner: 1:1 square at 0.85 of the height, centred.
    boxH = gameHeight * 0.85;
    boxW = boxH;
  } else {
    // Native fruit-slice / target-attack: 0.4w × 0.8h, centred.
    boxW = gameWidth * 0.4;
    boxH = gameHeight * 0.8;
  }
  const boxX = cx - boxW / 2;
  const boxY = cy - boxH / 2;
  const borderW = Math.max(3, gameHeight * 0.005);
  return {
    cx, cy, boxX, boxY, boxW, boxH, borderW,
  };
}

/** Start "fruit" target inside the stand zone — shared by fruit-slice draw + slice-detection. */
export interface RemoteCalibStartFruitMetrics {
  readonly cx: number;
  readonly cy: number;
  readonly r: number;
}

export function getRemoteCalibStartFruitMetrics(
  layout: RemoteViewLayout,
  kind: CalibStandZoneKind = 'full',
): RemoteCalibStartFruitMetrics {
  const {
    boxX, boxY, boxW, boxH,
  } = getRemoteCalibStandZoneMetrics(layout, kind);
  return {
    cx: boxX + boxW / 2,
    cy: boxY + boxH * 0.53,
    r: Math.min(boxW, boxH) * 0.2,
  };
}

export function isPointInsideRemoteCalibStandZone(
  px: number,
  py: number,
  layout: RemoteViewLayout,
  kind: CalibStandZoneKind = 'full',
): boolean {
  const {
    boxX, boxY, boxW, boxH,
  } = getRemoteCalibStandZoneMetrics(layout, kind);
  return px >= boxX && px <= boxX + boxW && py >= boxY && py <= boxY + boxH;
}
